import type { SpeechRecognitionOptions } from "../types/audio";
import { VOICE_CONFIG } from "../types/audio";
import { getAzureToken } from "./azureTokenClient";

import type * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";

type SpeechSDKModule = typeof SpeechSDK;

let cachedSDK: SpeechSDKModule | null = null;

async function loadSpeechSDK(): Promise<SpeechSDKModule> {
  if (cachedSDK) return cachedSDK;
  cachedSDK = await import("microsoft-cognitiveservices-speech-sdk");
  return cachedSDK;
}

class AzureSpeechService {
  private recognizer: SpeechSDK.SpeechRecognizer | null = null;
  private isRecognizing = false;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private noSpeechTimer: ReturnType<typeof setTimeout> | null = null;
  private sdk: SpeechSDKModule | null = null;

  private async ensureSDK(): Promise<SpeechSDKModule> {
    if (!this.sdk) this.sdk = await loadSpeechSDK();
    return this.sdk;
  }

  private async buildSpeechConfig(): Promise<SpeechSDK.SpeechConfig> {
    const sdk = await this.ensureSDK();
    const { token, region } = await getAzureToken();
    return sdk.SpeechConfig.fromAuthorizationToken(token, region);
  }

  async startRecognition(options: SpeechRecognitionOptions): Promise<void> {
    const {
      language,
      onInterimResult,
      onFinalResult,
      onError,
      onStart,
      onEnd,
      onTimeout,
      silenceTimeout = 3000,
      noSpeechTimeout,
    } = options;

    const effectiveNoSpeechTimeout = noSpeechTimeout ?? Math.max(silenceTimeout, 10000);

    if (this.isRecognizing) {
      return;
    }

    try {
      const sdk = await this.ensureSDK();

      const config = await this.buildSpeechConfig();
      const voiceConfig = VOICE_CONFIG[language];
      config.speechRecognitionLanguage = voiceConfig.locale;

      const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
      this.recognizer = new sdk.SpeechRecognizer(config, audioConfig);

      const recognizer = this.recognizer;

      recognizer.recognizing = (_sender, event) => {
        if (event.result.reason === sdk.ResultReason.RecognizingSpeech) {
          this.clearNoSpeechTimer();
          onInterimResult?.(event.result.text);
          this.resetSilenceTimer(silenceTimeout, onTimeout);
        }
      };

      recognizer.recognized = (_sender, event) => {
        if (event.result.reason === sdk.ResultReason.RecognizedSpeech) {
          this.clearNoSpeechTimer();
          onFinalResult?.(event.result.text);
          this.resetSilenceTimer(silenceTimeout, onTimeout);
        } else if (event.result.reason === sdk.ResultReason.NoMatch) {
          this.clearNoSpeechTimer();
          this.resetSilenceTimer(silenceTimeout, onTimeout);
        }
      };

      recognizer.canceled = (_sender, event) => {
        if (event.reason === sdk.CancellationReason.Error) {
          const error = new Error(`Recognition error: ${event.errorDetails}`);
          onError?.(error);
        }
        void this.stopRecognition();
      };

      recognizer.sessionStopped = () => {
        this.isRecognizing = false;
        onEnd?.();
        this.cleanupRecognizer();
      };

      await new Promise<void>((resolve, reject) => {
        recognizer.startContinuousRecognitionAsync(resolve, (errorDetails) =>
          reject(new Error(errorDetails)),
        );
      });
      this.isRecognizing = true;
      onStart?.();
      this.armNoSpeechTimer(effectiveNoSpeechTimeout, onTimeout);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      this.cleanupRecognizer();
      throw err;
    }
  }

  private resetSilenceTimer(timeout: number, onTimeout?: () => void): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      if (this.isRecognizing) {
        void this.stopRecognition();
        onTimeout?.();
      }
    }, timeout);
  }

  private armNoSpeechTimer(timeout: number, onTimeout?: () => void): void {
    this.clearNoSpeechTimer();
    this.noSpeechTimer = setTimeout(() => {
      if (this.isRecognizing) {
        void this.stopRecognition();
        onTimeout?.();
      }
    }, timeout);
  }

  private clearNoSpeechTimer(): void {
    if (this.noSpeechTimer) {
      clearTimeout(this.noSpeechTimer);
      this.noSpeechTimer = null;
    }
  }

  async stopRecognition(): Promise<void> {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    this.clearNoSpeechTimer();
    if (this.recognizer && this.isRecognizing) {
      const recognizer = this.recognizer;
      try {
        await new Promise<void>((resolve, reject) => {
          recognizer.stopContinuousRecognitionAsync(resolve, (errorDetails) =>
            reject(new Error(errorDetails)),
          );
        });
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
      this.isRecognizing = false;
      this.cleanupRecognizer();
    }
  }

  private cleanupRecognizer(): void {
    if (this.recognizer) {
      this.recognizer.close();
      this.recognizer = null;
    }
  }
}

export const azureSpeechService = new AzureSpeechService();
